/**
 * Apply widget link color on <a> tags and drop CKEditor "default-like" inline
 * colors inside links (black / widget text color) so linksColor can win.
 * Intentional non-default colors (e.g. purple) are kept.
 */

/**
 * @param {number} h
 * @param {number} s 0–100
 * @param {number} l 0–100
 * @returns {string} rgb(r,g,b)
 */
function hsl_to_rgb_token(h, s, l) {
    const sat = s / 100;
    const light = l / 100;
    const a = sat * Math.min(light, 1 - light);
    const f = (n) => {
        const k = (n + h / 30) % 12;
        const color = light - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color);
    };
    return `rgb(${f(0)},${f(8)},${f(4)})`;
}

/**
 * @param {string|null|undefined} value
 * @returns {string|null} comparable rgb(...) token
 */
export function normalize_css_color(value) {
    if (value == null)
        return null;
    let v = String(value).trim().toLowerCase().replace(/\s+/g, '');
    if (!v)
        return null;

    if (v === 'black')
        return 'rgb(0,0,0)';
    if (v === 'white')
        return 'rgb(255,255,255)';

    const hex = v.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/);
    if (hex) {
        let h = hex[1];
        if (h.length === 3)
            h = h.split('').map((c) => c + c).join('');
        if (h.length === 8)
            h = h.slice(0, 6);
        return `rgb(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)})`;
    }

    const rgb = v.match(/^rgba?\((\d+),(\d+),(\d+)/);
    if (rgb)
        return `rgb(${rgb[1]},${rgb[2]},${rgb[3]})`;

    const hsl = v.match(/^hsla?\((\d+(?:\.\d+)?),(\d+(?:\.\d+)?)%?,(\d+(?:\.\d+)?)%?/);
    if (hsl)
        return hsl_to_rgb_token(Number(hsl[1]), Number(hsl[2]), Number(hsl[3]));

    return null;
}

/**
 * @param {string} color
 * @param {string|null|undefined} text_color
 * @returns {boolean}
 */
export function is_default_like_color(color, text_color) {
    const normalized = normalize_css_color(color);
    if (!normalized)
        return false;
    if (normalized === 'rgb(0,0,0)')
        return true;
    const text_normalized = normalize_css_color(text_color || '#000000');
    return Boolean(text_normalized && normalized === text_normalized);
}

/**
 * Remove color declarations that are default-like from a CSS style string.
 * @param {string} style
 * @param {string|null|undefined} text_color
 * @returns {string}
 */
function strip_default_like_color_from_style(style, text_color) {
    return String(style || '')
        .replace(/(?:^|;)\s*color\s*:\s*([^;]+)/gi, (match, color_value) => {
            if (is_default_like_color(color_value, text_color))
                return match.startsWith(';') ? ';' : '';
            return match;
        })
        .replace(/^;\s*/, '')
        .replace(/;\s*$/, '')
        .replace(/;;+/g, ';')
        .trim();
}

/**
 * @param {string} html
 * @param {string|null|undefined} text_color
 * @returns {string}
 */
function strip_default_like_colors_in_markup(html, text_color) {
    return String(html || '').replace(/\sstyle\s*=\s*(["'])(.*?)\1/gi, (full, quote, style) => {
        const color_match = String(style).match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
        if (!color_match || !is_default_like_color(color_match[1], text_color))
            return full;
        const next = strip_default_like_color_from_style(style, text_color);
        if (!next)
            return '';
        return ` style=${quote}${next}${quote}`;
    });
}

/**
 * Set or replace the color declaration on an opening <a ...> attributes string.
 * @param {string} attrs
 * @param {string} links_color
 * @returns {string}
 */
function with_anchor_links_color(attrs, links_color) {
    const color_decl = `color: ${links_color};`;
    if (/\sstyle\s*=/i.test(attrs)) {
        return attrs.replace(/\sstyle\s*=\s*(["'])(.*?)\1/i, (_m, quote, style) => {
            const without_color = String(style || '')
                .replace(/(?:^|;)\s*color\s*:\s*[^;]+;?/gi, '')
                .replace(/^;\s*/, '')
                .trim();
            const next = without_color ? `${color_decl} ${without_color}` : color_decl;
            return ` style=${quote}${next}${quote}`;
        });
    }
    return ` style="${color_decl}"${attrs}`;
}

/**
 * @param {string} html
 * @param {string|null|undefined} links_color
 * @param {string|null|undefined} text_color
 * @returns {string}
 */
export function apply_email_links_color(html, links_color, text_color) {
    if (!html || !links_color)
        return html || '';

    return String(html).replace(/<a(\s[^>]*)?>([\s\S]*?)<\/a>/gi, (_full, attrs, inner) => {
        const next_attrs = with_anchor_links_color(attrs || '', links_color);
        const next_inner = strip_default_like_colors_in_markup(inner, text_color);
        return `<a${next_attrs}>${next_inner}</a>`;
    });
}
